const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';

export function normalizeNumberInput(value) {
  return String(value ?? '')
    .replace(/[۰-۹]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)))
    .replace(/[٬,\s]/g, '')
    .replace('٫', '.');
}

export function finiteNumber(value) {
  const normalized = normalizeNumberInput(value);
  if (normalized.trim() === '') return null;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

export function quantityFromInput(mode, amount, priceUsd, usdIrtRate) {
  const entered = finiteNumber(amount);
  const price = finiteNumber(priceUsd);
  const rate = finiteNumber(usdIrtRate);
  if (entered === null || entered <= 0) return null;
  if (mode === 'asset') return entered;
  if (price === null || price <= 0) return null;
  if (mode === 'usd') return entered / price;
  if (mode === 'irt' && rate !== null && rate > 0) return entered / (price * rate);
  return null;
}

export function transactionTotals(quantity, priceUsd, usdIrtRate) {
  const q = finiteNumber(quantity);
  const price = finiteNumber(priceUsd);
  const rate = finiteNumber(usdIrtRate);
  if (q === null || price === null || rate === null) return { usd: null, irt: null };
  return { usd: q * price, irt: q * price * rate };
}

function optionalNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

// Mirrors the portfolio service: P&L = value + sell revenue - buy cost, percent against buy cost.
export function applyTransactionChange(asset, previous = null, next = null) {
  const totals = {
    quantity: optionalNumber(asset.quantity) ?? 0,
    totalSpentUsd: optionalNumber(asset.totalSpentUsd) ?? 0, totalSpentIrt: optionalNumber(asset.totalSpentIrt) ?? 0,
    totalRevenueUsd: optionalNumber(asset.totalRevenueUsd) ?? 0, totalRevenueIrt: optionalNumber(asset.totalRevenueIrt) ?? 0,
  };
  for (const [transaction, sign] of [[previous, -1], [next, 1]]) {
    if (!transaction) continue;
    const quantity = finiteNumber(transaction.amount);
    const value = transactionTotals(quantity, transaction.unitPriceUsd, transaction.usdIrtRate);
    if (quantity === null || value.usd === null) continue;
    const sell = transaction.type === 'sell';
    totals.quantity += sign * (sell ? -quantity : quantity);
    totals[sell ? 'totalRevenueUsd' : 'totalSpentUsd'] += sign * value.usd;
    totals[sell ? 'totalRevenueIrt' : 'totalSpentIrt'] += sign * value.irt;
  }
  const held = optionalNumber(asset.quantity);
  const unitValue = (value, price) => (held && optionalNumber(value) !== null ? Number(value) / held : optionalNumber(price));
  const unitUsd = unitValue(asset.valueUsd, asset.priceUsd);
  const unitIrt = unitValue(asset.valueIrt, asset.priceIrt);
  const valueUsd = unitUsd === null ? null : totals.quantity * unitUsd;
  const valueIrt = unitIrt === null ? null : totals.quantity * unitIrt;
  const pnlUsd = valueUsd === null ? null : valueUsd + totals.totalRevenueUsd - totals.totalSpentUsd;
  const pnlIrt = valueIrt === null ? null : valueIrt + totals.totalRevenueIrt - totals.totalSpentIrt;
  return {
    ...totals, valueUsd, valueIrt, pnlUsd, pnlIrt,
    pnlPercentUsd: pnlUsd !== null && totals.totalSpentUsd > 0 ? (pnlUsd / totals.totalSpentUsd) * 100 : null,
    pnlPercentIrt: pnlIrt !== null && totals.totalSpentIrt > 0 ? (pnlIrt / totals.totalSpentIrt) * 100 : null,
  };
}

// Portfolio totals are sums of holdings, so shift them by the changed holding's difference.
export function portfolioTotalsDelta(detail, before = null, after = null) {
  const patch = { pnlPercentUsd: null, pnlPercentIrt: null };
  for (const key of ['valueUsd', 'valueIrt', 'pnlUsd', 'pnlIrt']) {
    const current = optionalNumber(detail[key]);
    patch[key] = current === null ? null : current + (optionalNumber(after?.[key]) ?? 0) - (optionalNumber(before?.[key]) ?? 0);
  }
  return patch;
}

export function stableSortAssets(assets, key = 'valueUsd-desc') {
  const [field, direction = 'desc'] = key.split('-');
  const fields = {
    valueUsd: 'valueUsd', valueIrt: 'valueIrt', pnlUsd: 'pnlUsd', pnlIrt: 'pnlIrt',
    pnlPercentUsd: 'pnlPercentUsd', pnlPercentIrt: 'pnlPercentIrt', name: 'name',
  };
  const property = fields[field] || 'valueUsd';
  return assets.map((asset, index) => ({ asset, index })).sort((left, right) => {
    const a = left.asset[property]; const b = right.asset[property];
    const missingA = a === null || a === undefined || a === '';
    const missingB = b === null || b === undefined || b === '';
    if (missingA !== missingB) return missingA ? 1 : -1;
    let result = property === 'name'
      ? String(a).localeCompare(String(b), 'fa')
      : Number(a) - Number(b);
    if (direction === 'desc') result *= -1;
    return result || left.index - right.index;
  }).map(({ asset }) => asset);
}
